<template>
  <!-- ----------------- -->
  <!-- TOP ROW (HEADER)  -->
  <!-- ----------------- -->
  <div class="mt-3">
    <div class="mt-3">
      <div class="row mb-0">
        <p class="bb-sheet-title">{{ skill_title }}</p>
      </div>
    </div>
    <!-- ------------------------- -->
    <!-- OVERLAY: skill side panel -->
    <!-- ------------------------- -->
    <SkillSidePanel
      v-if="isSidePanelOpen"
      :skill="character_skill"
      :activeTab="activeTab"
      :character="character"
      :max_skill="max_skill"
      @add-character-skill="$emit('add-character-skill', $event)"
      @update-character-skill="$emit('update-character-skill', $event)"
      @delete-character-skill="$emit('delete-character-skill', $event)"
      @close-skill-panel="handleSkillPanelClose"
    />
    <!-- ------------------------ -->
    <!-- NOTIFICATIONS            -->
    <!-- ------------------------ -->
    <notifications
      width="500"
      group="bb-confirm"
      :speed="100"
      :duration="1000"
      position="top right"
      classes="vue-notification"
    />
    <!-- ------------------ -->
    <!--     skill table    -->
    <!-- ------------------ -->
    <div v-if="skills">
      <TableSkills
        ref="TableSkills"
        v-if="skills && character"
        :headings="headings"
        :skill_type="skill_type"
        :skills="skills"
        :character="character"
        :max_skill="max_skill"
        @adjust-character-skill="forwardEmitOpenAdjs"
        @optional-adjust-character-skill="forwardEmitOpenOptAdjs"
        @show-character-skill="handleShowSkillPanel"
        @toggle-favourite="handleToggleFavourite"
        @delete-character-skill="$emit('delete-character-skill', $event)"
      />
    </div>
  </div>
  <!-- <div v-if="!loadReady" class="bb-loader"></div> -->
  <!-- </div> -->
</template>

<script>
//========================================================
// scripts
//---------
import { inject } from 'vue'
import { mapActions, mapGetters } from 'vuex'

import useDeleteCharacterSkill from '../composables/useDeleteCharacterSkill'

import SkillSidePanel from '../components/SkillSidePanel.vue'
import TableSkills from './TableSkills.vue'

import getSkillSummaryCols from '../helpers/getSkillSummaryCols'
import getSkillDetailCols from '../helpers/getSkillDetailCols'
import showNotification from '../helpers/showNotification'
import {
  getFavouriteSkillBucket,
  getFavouriteSkillBucketLimits,
} from '../helpers/favouriteSkillBuckets'
import { evaluateWeaponUsability } from '../helpers/weaponUsabilityCore'
import calcSkillValues from '../helpers/calcSkillValues'

import handleError from '../helpers/handleError'
import isEmpty from '../helpers/isEmpty'

export default {
  name: 'CharacterSheetSkills',
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // components
  //------------
  components: {
    TableSkills,
    SkillSidePanel,
  },
  //========================================================
  // props
  //-------
  props: {
    character: {
      type: Object,
      required: true,
    },
    skill_type: {
      type: String,
      required: true,
    },
    max_skill: {
      type: Number,
      required: true,
    },
    errorFields: {
      type: Array,
      required: true,
    },
    toggleDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    activeTab: {
      type: String,
      required: false,
      default: '',
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      endpoint: '',
      local_slug: '',

      deleteInd: false,
      loadReady: false,
      initialLoad: true,
      isSidePanelOpen: false,

      character_skill: null,

      skills: [],
      errors: [],

      name: 'SkillList',
      headings: [
        {
          label: '#',
          field: 'rowNumber',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
        },
        {
          id: 5,
          label: 'Skill',
          field: 'name',
          width: '12em',
          filterable: false,
          sortable: false,
          formatFn: (value, row) => {
            return row.display_name || row.name
          },
        },
        {
          id: 150,
          label: 'Type',
          field: 'type',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 6,
          label: 'Spell Type',
          field: 'spell_type',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: this.skill_type !== 'Casting',
        },
        {
          id: 7,
          label: 'Integrated Rune',
          field: 'integrated_rune_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: this.skill_type !== 'Rune' && this.skill_type !== 'Casting',
        },
        {
          id: 8,
          label: 'Spell Count',
          field: 'spell_count',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: this.skill_type !== 'Casting',
          formatFn: this.global.hideZeroMinusValues,
        },
        {
          id: 10,
          label: 'Cur',
          field: 'cur',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 15,
          label: '',
          field: 'fill1',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 20,
          label: 'Cur (Unadj)',
          field: 'cur_no_adjs',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 25,
          label: 'Incr',
          field: 'incr_percent',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 30,
          label: '',
          field: 'fill2',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 35,
          label: 'Ticks',
          field: 'ticks',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 40,
          label: 'Training',
          field: 'training',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 45,
          label: 'Base',
          field: 'base',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 50,
          label: 'Formula',
          field: 'formula',
          width: '10em',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 110,
          label: 'Usable',
          field: 'usable_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 52,
          label: 'Favourite',
          field: 'favourite_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 55,
          label: 'Parent Skill',
          field: 'parent_name',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 60,
          label: '',
          field: 'fill3',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 65,
          label: 'Applied Adjs Total',
          field: 'adjs_tot',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: (value, row) =>
            this.hideZeroValuesWhereNoAdjsElseSigned(value, row),
        },
        {
          id: 67,
          label: 'Applied Adjs Count',
          field: 'adjs_cnt',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 70,
          label: 'Applied Adjs',
          field: 'action_adjust',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
        },
        {
          id: 30,
          label: '',
          field: 'fill4',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 75,
          label: 'Opt Adjs Count',
          field: 'adjs_cnt_opt',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.global.hideZeroValues,
        },
        {
          id: 80,
          label: 'Opt Adjs',
          field: 'action_adjust_opt',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
        },
        {
          id: 85,
          label: 'Delete',
          field: 'action_delete',
          filterable: false,
          sortable: false,
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 90,
          label: '',
          field: 'fill5',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 95,
          label: 'Max Ticks',
          field: 'ticks_max',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: false,
          formatFn: this.global.hideZeroMinusValues,
        },
        {
          id: 100,
          label: 'Capped Ticks',
          field: 'capped_ticks_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 105,
          label: 'Capped Skill',
          field: 'capped_skill_image',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
        },
        {
          id: 112,
          label: '',
          field: 'fill6',
          filterable: false,
          sortable: false,
          tdClass: 'bb-is-fill',
          thClass: 'bb-is-fill',
        },
        {
          id: 115,
          label: 'Adjs Total (Skill)',
          field: 'adjs_tot_character_skill',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.global.hideZeroValuesButSigned,
        },
        {
          id: 120,
          label: 'Applied Adj Reasons (Skill)',
          field: 'adjs_reasons_character_skill',
          tdClass: 'text-nowrap adj-reasons-nowrap',
          sortable: false,
        },
        {
          id: 122,
          label: 'Optional Adj Reasons (Skill)',
          field: 'adjs_reasons_character_skill_opt',
          tdClass: 'text-nowrap adj-reasons-nowrap',
          sortable: false,
        },
        {
          id: 125,
          label: 'Adjs Total (Chars)',
          field: 'adjs_tot_characteristic_skill',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.global.hideZeroValuesButSigned,
        },
        {
          id: 130,
          label: 'Adj Reasons (Chars)',
          field: 'adjs_reasons_characteristic_skill',
          tdClass: 'text-nowrap adj-reasons-nowrap',
          sortable: false,
        },
        {
          id: 135,
          label: 'Adjs Total (Runes)',
          field: 'adjs_tot_integrated_rune_skill',
          type: 'number',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          formatFn: this.global.hideZeroValuesButSigned,
        },
        {
          id: 140,
          label: 'Adj Reasons (Runes)',
          field: 'adjs_reasons_integrated_rune_skill',
          tdClass: 'text-nowrap adj-reasons-nowrap',
          sortable: false,
        },
        {
          id: 145,
          label: 'Category',
          field: 'category',
          hidden: true,
        },
        {
          id: 155,
          label: 'Group',
          field: 'group',
          hidden: true,
        },
        {
          id: 160,
          label: 'Default',
          field: 'default',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 165,
          label: 'Tickable',
          field: 'tickable',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 170,
          label: 'Dex-Based',
          field: 'dex_based',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 175,
          label: 'Capped Ticks Flag',
          field: 'capped_ticks',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 180,
          label: 'Capped Skill Flag',
          field: 'capped_skill',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 185,
          label: 'Id',
          field: 'id',
          type: 'number',
          tdClass: 'text-nowrap',
          hidden: true,
        },
        {
          id: 190,
          label: 'Parent Formula',
          field: 'parent_formula',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 195,
          label: 'Formula Arg1',
          field: 'formula_arg1',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 200,
          label: 'Formula Oper1',
          field: 'formula_oper1',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 205,
          label: 'Formula Arg2',
          field: 'formula_arg2',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 210,
          label: 'Formula Oper2',
          field: 'formula_oper2',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 215,
          label: 'Formula Arg3',
          field: 'formula_arg3',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 220,
          label: 'Parent Cur',
          field: 'parent_cur',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 225,
          label: 'Parent Cur No Adjs',
          field: 'parent_cur_no_adjs',
          tdClass: 'text-center',
          hidden: true,
        },
        {
          id: 230,
          label: 'Integrated',
          field: 'active',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
        {
          id: 235,
          label: 'Favourite Value',
          field: 'favourite',
          tdClass: 'text-center',
          filterable: false,
          sortable: false,
          hidden: true,
        },
      ],
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    toggleDetails(newVal) {
      this.toggleDetailsHandler(newVal)
    },
    skill_type() {
      this.updateSkillColumnWidth()
    },
    active_tab() {
      //------------------------------------------
      // show details or summary cols where table
      //------------------------------------------
      this.toggleDetailsHandler(this.toggleDetails)
    },
    //---------------------------------------------------------------------
    // watch character so adding/removing skills updates table immediately
    //---------------------------------------------------------------------
    character: {
      handler(newChar) {
        console.log('=====================================')
        console.log('WATCH CHARACTER SKILLS....')
        if (!newChar) return
        // reload filtered/sorted skills for the new/updated character
        this.loadCharacterSkillsByType(newChar)
      },
      deep: true,
      immediate: true,
    },
  },
  //========================================================
  // computed
  //----------
  computed: {
    //--------
    // mapped
    //--------
    ...mapGetters('combat', {
      getConfig: 'getConfig',
    }),
    ...mapGetters('characters', {
      getCharacterSkills: 'getCharacterSkills',
      getStaticMeleeWeapons: 'getStaticMeleeWeapons',
      getStaticRangedWeapons: 'getStaticRangedWeapons',
      getStaticItemsByType: 'getStaticItemsByType',
      getStaticItemById: 'getStaticItemById',
    }),
    // bridge prop name to helpers expecting `active_tab`
    active_tab() {
      return this.activeTab
    },
    skill_title() {
      if (this.skill_type == 'Specialism') {
        return 'Specialisms'
      } else if (this.skill_type == 'Lore') {
        return 'Lores'
      } else if (this.skill_type == 'Language') {
        return 'Languages'
      } else {
        return this.skill_type + ' Skills'
      }
    },
    api_request() {
      return window.bbAPIRequest(this.endpoint, this.local_slug, this.deleteInd)
    },
    isInvertedSidePanelOpen() {
      return !this.isSidePanelOpen
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    ...mapActions('characters', {
      loadStaticSkills: 'loadStaticSkills',
    }),
    //--------------------------------------------
    // hide zero values where no adjs else signed
    //--------------------------------------------
    hideZeroValuesWhereNoAdjsElseSigned(value, row) {
      if (value == 0) {
        if (this.getAppliedAdjustmentCount(row) > 0) {
          return `+${value}`
        }
        return ''
      }
      if (value > 0) {
        return `+${value}`
      }
      return value
    },
    getAppliedAdjustmentCount(skill = {}) {
      const hasValue = (adj) => Number(adj?.adj_value || 0) !== 0
      const characterSkillCount = (skill.character_skill_adjs || []).filter(
        (adj) => adj?.applied === true && hasValue(adj),
      ).length
      const characteristicSkillCount = (
        skill.characteristic_skill_adjs || []
      ).filter(hasValue).length
      const integratedRuneSkillCount = (
        skill.integrated_rune_skill_adjs || []
      ).filter(hasValue).length

      return (
        characterSkillCount +
        characteristicSkillCount +
        integratedRuneSkillCount
      )
    },
    getFavouriteBucketLimits() {
      const config =
        typeof this.getConfig === 'function' ? this.getConfig() : this.getConfig

      return getFavouriteSkillBucketLimits(config)
    },
    getFavouriteBucket(skill) {
      return getFavouriteSkillBucket(skill, this.getFavouriteBucketLimits())
    },
    handleToggleFavourite(skill) {
      if (!skill) return

      const nextFavourite = !skill.favourite

      if (nextFavourite) {
        const bucket = this.getFavouriteBucket(skill)

        if (bucket) {
          const existingCount = (this.character?.skills || []).filter(
            (candidate) =>
              candidate?.id !== skill.id &&
              Boolean(candidate?.favourite) &&
              bucket.matches(candidate),
          ).length

          if (existingCount >= bucket.limit) {
            showNotification(
              `Unable to set favourite - Max of ${bucket.limit} reached for ${bucket.label}. Remove existing favourite to proceed`,
            )
            return
          }
        }
      }

      this.$emit('update-character-skill', {
        type: 'skills',
        id: skill.id,
        name: skill.name,
        key: 'favourite',
        value: nextFavourite,
      })
    },
    // ensure the Skill column is wider for Languages
    updateSkillColumnWidth() {
      const col = this.headings.find((h) => h.field === 'name')
      if (!col) return
      col.width = this.skill_type === 'Language' ? '16em' : '12em'
    },
    getSkillName(skill) {
      if (skill.display_name) {
        return skill.display_name
      }
      return skill.name
    },
    getWeaponStaticSkillsByType(skillType) {
      if (skillType === 'Melee') {
        return Array.isArray(this.getStaticMeleeWeapons)
          ? this.getStaticMeleeWeapons
          : []
      }

      if (skillType === 'Ranged') {
        return Array.isArray(this.getStaticRangedWeapons)
          ? this.getStaticRangedWeapons
          : []
      }

      return []
    },
    getWeaponSkillUsability(skill, character, skillType) {
      const currentStr = Number(character?.str_cur ?? null)
      const currentDex = Number(character?.dex_cur ?? null)

      const staticSkills = this.getWeaponStaticSkillsByType(skillType)
      const normalizeName = (value) =>
        (value || '').toString().trim().toLowerCase()
      const normalizeId = (value) => {
        if (value === null || value === undefined) return null
        const normalized = String(value).trim()
        return normalized === '' ? null : normalized
      }
      const skillName = normalizeName(skill?.name)
      const skillDisplayName = normalizeName(skill?.display_name)
      let rowSkillStaticId =
        normalizeId(skill?.skill) ?? normalizeId(skill?.skill_id)

      if (rowSkillStaticId === null) {
        const getStaticSkillByName =
          this.$store?.getters?.['characters/getStaticSkillByName']

        if (typeof getStaticSkillByName === 'function') {
          const byDisplay = skill?.display_name
            ? getStaticSkillByName(skill.display_name)
            : null
          const byName = skill?.name ? getStaticSkillByName(skill.name) : null

          rowSkillStaticId =
            normalizeId(byDisplay?.id) ?? normalizeId(byName?.id)
        }
      }

      const hasExactNameMatch = ({ linkedName, linkedDisplayName }) => {
        // If the row has a display name, treat that as authoritative to avoid
        // conflating sibling skills that share the same base name.
        if (skillDisplayName !== '') {
          return linkedDisplayName === skillDisplayName
        }

        if (skillName === '') {
          return false
        }

        return linkedName === skillName || linkedDisplayName === skillName
      }

      const isMatchingStaticSkill = (linkedSkill) => {
        const linkedStaticId =
          normalizeId(linkedSkill?.skill) ??
          normalizeId(linkedSkill?.skill_id) ??
          normalizeId(linkedSkill?.id)

        const linkedName = normalizeName(linkedSkill?.name)
        const linkedDisplayName = normalizeName(linkedSkill?.display_name)
        const hasNameMatch = hasExactNameMatch({
          linkedName,
          linkedDisplayName,
        })

        // Prefer exact static skill ID matching where available.
        if (rowSkillStaticId && linkedStaticId) {
          return linkedStaticId === rowSkillStaticId
        }

        if (rowSkillStaticId) {
          return false
        }

        return hasNameMatch
      }

      const isMatchingItemSkill = (linkedSkill) => {
        const linkedStaticId =
          normalizeId(linkedSkill?.skill) ?? normalizeId(linkedSkill?.skill_id)

        const linkedName = normalizeName(linkedSkill?.name)
        const linkedDisplayName = normalizeName(linkedSkill?.display_name)
        const hasNameMatch = hasExactNameMatch({
          linkedName,
          linkedDisplayName,
        })

        // Prefer exact static skill ID matching where available.
        if (rowSkillStaticId) {
          return !!linkedStaticId && linkedStaticId === rowSkillStaticId
        }

        return hasNameMatch
      }

      const staticSkill = staticSkills.find((candidate) => {
        if (!candidate) return false

        return isMatchingStaticSkill(candidate)
      })

      if (rowSkillStaticId === null) {
        rowSkillStaticId = normalizeId(staticSkill?.id)
      }

      if (!staticSkill) {
        // continue and derive requirements from weapon item-skill links
      }

      const itemType = skillType === 'Melee' ? 'Melee Weapon' : 'Ranged Weapon'
      const characterItems = Array.isArray(character?.items)
        ? character.items
        : []
      const staticItems = characterItems
        .map((characterItem) => {
          const staticItemId = characterItem?.item ?? null
          if (!staticItemId || typeof this.getStaticItemById !== 'function') {
            return null
          }

          return this.getStaticItemById(staticItemId)
        })
        .filter((item) => item && item.type === itemType)

      // Primary source: item-skill links (same source as item usability).
      const rowLinkedSkills = []
      for (const item of staticItems || []) {
        const linkedSkills = Array.isArray(item?.skills) ? item.skills : []

        for (const linkedSkill of linkedSkills) {
          if (!isMatchingItemSkill(linkedSkill)) continue

          rowLinkedSkills.push(linkedSkill)
        }
      }

      // Fallback source: static skill mins where no linked mins were found.
      const rowStaticSkills =
        rowLinkedSkills.length > 0
          ? rowLinkedSkills
          : staticSkill
            ? [staticSkill]
            : []

      const characterSkills = Array.isArray(character?.skills)
        ? character.skills
        : []

      const result = evaluateWeaponUsability({
        staticSkills: rowStaticSkills,
        characterSkills,
        itemType,
        strCur: currentStr,
        dexCur: currentDex,
      })

      if (result.usable_status === 'Yes') {
        return {
          usable_status: 'Yes',
          unusable_reasons: 'n/a',
        }
      }

      return {
        usable_status: result.usable_status,
        unusable_reasons:
          result.unusable_reasons ||
          'Required minimum characteristics are not met.',
      }
    },
    getColumnSelectionTab() {
      if (this.skill_type === 'Casting') {
        return 'skills-casting'
      }

      if (this.skill_type === 'Melee') {
        return 'skills-melee-weapons'
      }

      if (this.skill_type === 'Ranged') {
        return 'skills-ranged-weapons'
      }

      return this.active_tab
    },
    getCastingSpellType(skill, allSpells) {
      const skillName = (skill.name || '').toString().trim().toLowerCase()
      const skillType = (skill.type || '').toString().trim().toLowerCase()
      if (skillName.includes('runecasting')) {
        return 'Rune'
      }
      if (skillName.includes('sorcery')) {
        return 'Sorcery'
      }
      if (skillName.includes('divine') || skillName.includes('devotion')) {
        return 'Divine'
      }
      if (skillType === 'rune') {
        return 'Rune'
      }
      if (skillType === 'sorcery') {
        return 'Sorcery'
      }
      if (skillType === 'divine' || skillType === 'devotion') {
        return 'Divine'
      }

      const skillId = skill.id != null ? String(skill.id) : null
      if (!skillId) return ''

      const linkedSpellTypes = allSpells
        .filter(
          (spell) =>
            spell &&
            spell.casting_skill != null &&
            String(spell.casting_skill) === skillId &&
            spell.type,
        )
        .map((spell) => spell.type)

      return linkedSpellTypes[0] || ''
    },
    //========================================================
    // delete skill harness
    //----------------------
    async deleteCharacterSkillHarness(parms) {
      console.log('-------------------------------')
      console.log('IN deleteCharacterSkillHarness')
      this.loadReady = false
      //---------------
      // get skill
      //---------------
      // const skill = getSkillByIndex(delete_index)
      //-----------------------------
      // call delete character skill
      //-----------------------------
      let call = useDeleteCharacterSkill(parms.id)
      await call
        .then(() => {
          //---------------
          // reload skills
          //---------------
          if (this.skill_type === 'Casting') {
            this.loadCharacterSkillsByType(this.character)
          } else {
            this.skills = this.getCharacterSkills(this.skill_type)
          }
          //--------------
          // show message
          //--------------
          let msg = 'Character skill ' + parms.name + ' deleted.'
          showNotification(msg)
        })
        .catch((err) => {
          handleError(err, 18)
        })
    },
    //========================================================
    // load character skills by type
    //-------------------------------
    loadCharacterSkillsByType(character) {
      console.log('-------------------------------')
      console.log('IN loadCharacterSkillsByType skill_type = ', this.skill_type)
      // console.log(' this.character.runes = ', this.character.runes)
      let sourceSkills
      const allSpells = Array.isArray(character.spells) ? character.spells : []
      const recalculatedSkills = Array.isArray(character.skills)
        ? character.skills.map((skill) => ({
            ...skill,
            adjs_cnt: this.getAppliedAdjustmentCount(skill),
          }))
        : []

      recalculatedSkills
        .filter((skill) => isEmpty(skill?.parent_name))
        .forEach((skill) => {
          calcSkillValues({
            skill,
            character: { ...character, skills: recalculatedSkills },
            max_skill: this.max_skill,
          })
        })

      if (this.skill_type === 'All') {
        sourceSkills = recalculatedSkills
      } else if (this.skill_type === 'Casting') {
        // Casting tab: show explicit casting skills plus Sorcery-typed skills
        sourceSkills = recalculatedSkills.filter(
          (s) =>
            s.name === 'Runecasting' ||
            s.name === 'Sorcery Casting' ||
            s.type === 'Sorcery',
        )
      } else {
        sourceSkills = recalculatedSkills.filter(
          (s) => s.type == this.skill_type,
        )
      }

      this.skills = sourceSkills
        .map((s) => {
          const normalizedSkill = { ...s, spell_count: 0, spell_type: '' }
          //--------------------------------------
          // where type = specialism & has parent
          //--------------------------------------
          if (
            this.skill_type === 'Specialism' &&
            !isEmpty(normalizedSkill.parent_name)
          ) {
            normalizedSkill.formula = normalizedSkill.parent_formula
            // get parent cur value for tooltip on child cur
            const parentSkill = recalculatedSkills.find(
              (ps) => ps.name === normalizedSkill.parent_name,
            )
            normalizedSkill.parent_cur = parentSkill ? parentSkill.cur : null
            normalizedSkill.parent_cur_no_adjs = parentSkill
              ? parentSkill.cur_no_adjs
              : null
            //-------------------
            // where underlying type = rune
            //-------------------
          } else if (normalizedSkill.type === 'Rune') {
            console.log(
              'IN loadCharacterSkillsByType (rune-backed skill) = ',
              normalizedSkill,
            )
            console.log('PROCESSING SKILL = ', normalizedSkill.name)
            const integrated_rune = this.character.runes.find(
              (r) =>
                r.name === normalizedSkill.display_name &&
                r.integrated === true,
            )
            console.log('FOUND INTEGRATED RUNE = ', integrated_rune)
            normalizedSkill.integrated_rune = !!integrated_rune
          }

          if (this.skill_type === 'Casting') {
            normalizedSkill.spell_type = this.getCastingSpellType(
              normalizedSkill,
              allSpells,
            )

            const skillId =
              normalizedSkill.id != null ? String(normalizedSkill.id) : null
            const skillRuneId =
              normalizedSkill.character_rune != null
                ? String(normalizedSkill.character_rune)
                : null
            const skillRuneName = (
              normalizedSkill.display_name ||
              normalizedSkill.name ||
              ''
            )
              .toString()
              .trim()

            normalizedSkill.spell_count = allSpells.filter((spell) => {
              if (!spell) return false

              const spellCastingSkillId =
                spell.casting_skill != null ? String(spell.casting_skill) : null

              if (
                skillId &&
                spellCastingSkillId &&
                spellCastingSkillId === skillId
              ) {
                return true
              }

              if (normalizedSkill.name !== 'Runecasting') {
                return false
              }

              const sourceLabel = (spell.source || '').toString().toLowerCase()
              if (sourceLabel !== 'rune') return false

              const spellRuneId =
                (spell.character_rune_id != null
                  ? String(spell.character_rune_id)
                  : null) ||
                (spell.character_rune != null
                  ? String(spell.character_rune)
                  : null) ||
                (spell.source_id != null ? String(spell.source_id) : null)

              if (skillRuneId && spellRuneId) {
                return spellRuneId === skillRuneId
              }

              const spellRuneName = (spell.casting_rune_name || '')
                .toString()
                .trim()
              return skillRuneName !== '' && spellRuneName === skillRuneName
            }).length
          }

          if (this.skill_type === 'Melee' || this.skill_type === 'Ranged') {
            const weaponSkillUsability = this.getWeaponSkillUsability(
              normalizedSkill,
              character,
              this.skill_type,
            )
            normalizedSkill.usable_status = weaponSkillUsability.usable_status
            normalizedSkill.unusable_reasons =
              weaponSkillUsability.unusable_reasons
          }

          return normalizedSkill
        })
        .sort((a, b) => {
          if (this.skill_type === 'All') {
            const aName = a.display_name || a.name || ''
            const bName = b.display_name || b.name || ''
            return aName.localeCompare(bName)
          }
          if (this.skill_type === 'Rune') {
            return a.display_name.localeCompare(b.display_name)
          }
          if (this.skill_type === 'Casting') {
            const aName = a.display_name || a.name
            const bName = b.display_name || b.name
            return aName.localeCompare(bName)
          }
          return a.name.localeCompare(b.name)
        })
      console.log('PPPPPP SKILLS = ', this.skills)
    },
    //========================================================
    // toggle details handler
    //------------------------
    toggleDetailsHandler(toggle) {
      // console.log('-------------------------')
      // console.log('IN skills toggleDetailsHandler = ', toggle)
      // on = details / off = summary
      let i = 0
      let colNumbers = []
      const columnSelectionTab = this.getColumnSelectionTab()
      //
      if (toggle) {
        // console.log('TOGGLE DETAILS ON')
        colNumbers = getSkillDetailCols(columnSelectionTab, this.headings)
      } else {
        // console.log('TOGGLE DETAILS OFF')
        colNumbers = getSkillSummaryCols(columnSelectionTab, this.headings)
      }
      //---------------
      // hide all cols
      //---------------
      this.headings.map((a) => (a.hidden = true))
      //--------------------
      // show specific cols
      //--------------------
      while (i < colNumbers.length) {
        // console.log('SHOWING COL = ', colNumbers[i], " field = ", this.headings[colNumbers[i]].field)
        this.headings[colNumbers[i]].hidden = false
        i++
      }
    },
    //========================================================
    // handle show skill panel
    //-------------------------
    async handleShowSkillPanel(character_skill) {
      console.log('IN handleShowSkillPanel')

      if (!character_skill) {
        await this.loadStaticSkills({ force: true })
      }

      this.character_skill = character_skill
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
    //========================================================
    // handle scroll table
    //----------------------
    handleScrollTable(dir) {
      // console.log('IN SheetSkills handleScrollTable dir = ', dir)
      // call method on TableSkills via ref
      this.$refs.TableSkills.handleScrollTable(dir)
    },
    //========================================================
    // forwardEmitOpenAdjs
    //---------------------
    forwardEmitOpenAdjs(data) {
      this.$emit('adjust-character-skill', data)
    },
    //========================================================
    // forwardEmitOpenOptAdjs
    //---------------------
    forwardEmitOpenOptAdjs(data) {
      this.$emit('optional-adjust-character-skill', data)
    },
    //========================================================
    // handle skill panel close
    //--------------------------
    handleSkillPanelClose() {
      // console.log('IN handleSkillPanelClose...')
      // this.SetView('skills')
      this.isSidePanelOpen = this.isInvertedSidePanelOpen
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    // adjust Skill column width based on type
    this.updateSkillColumnWidth()
    //----------------------
    // prime toggle details
    //----------------------
    this.toggleDetailsHandler(this.toggleDetails)
    //-----------------------
    // load character skills
    //-----------------------
    this.loadCharacterSkillsByType(this.character)
    //========================================================
    // toggle skill details handler
    //------------------------------
    this.emitter.on('toggle-skill-details', () => {
      // console.log('IN skills toggle-skill-details')
      this.toggleDetailsHandler()
    })
  },
}
</script>
